import { makeAutoObservable, runInAction } from "mobx";
import type RootStore from "stores/RootStore";
import { loadErrorMessage } from "shared/loadOrError";
import { isAdmin } from "auth/tier";
import { canUseDataMapper } from "../rbac";
import { dataMapperSelection, dataMapperService, dataPlaneService } from "../dataMapperServices";
import { subscribeTabEvents, type DataMapperTabEventPayload } from "../tabEvents";
import { refusalReason, sameId, SQL_HOST_NOT_ALLOWED_MESSAGE, type DataMapperProfile } from "../dataMapper.types";
import type DataMapperSourceStore from "../Source/DataMapperSourceStore";
import {
  DM_EVENTS,
  DM_RESTORE_PATH,
  RESTORE_EVENTS,
  type BakInfo,
  type RestoreEvent,
  type RestoreJobView,
  type ServerDatabase,
} from "../Explorer/explorer.types";
import { isSessionEnded } from "stores/sessionGuard";

/** The Data Source route: the server this screen dials is the one filled in there. */
export const DATA_MAPPER_SOURCE_ROUTE = "Data Mapper Source";

/** Shown to anyone who is not an admin, and when the API answers 403. */
export const RESTORE_ADMIN_ONLY =
  "Restoring a backup, reading a backup's contents and listing a server's databases are for HQ administrators only.";

/** A database name the API accepts (data-mapper-restore's schema): letters, digits, underscores. */
export const RESTORE_NAME_RE = /^[A-Za-z0-9_]{1,128}$/;

/** At most this many byte-pages of database names are followed (each is a fresh dial). */
const MAX_DATABASE_PAGES = 20;

/**
 * A restore refusal as a sentence, chosen by its `reason` code and never by the
 * message text: the API's `data.reason` when the call itself was refused, and
 * the `reason` of a `restore-error` when the job failed in the background (the
 * same codes: database-exists, file-in-use, protected-database,
 * restore-in-progress, or db-error — data-plane/restore.ts). Anything else
 * shows the API's own message, which is already a sentence.
 */
export function restoreErrorSentence(reason: string | null, message: string, databaseName: string): string {
  const name = databaseName ? `"${databaseName}"` : "that name";
  switch (reason) {
    case "database-exists":
      return (
        `A database named ${name} already exists on this SQL Server. ` +
        'Choose another name, or turn on "Replace an existing database" to overwrite it.'
      );
    case "file-in-use":
      return (
        "A data or log file this restore would write belongs to another database on the SQL Server. " +
        "Restore under another database name so its files get new names."
      );
    case "protected-database":
      return `${name} is a system database or HQ's own database; a restore never replaces those. Choose another name.`;
    case "restore-in-progress":
      return "A restore is already running on this SQL Server. Wait for it to finish, then try again.";
    case "admin-only":
      return RESTORE_ADMIN_ONLY;
    case "sql-host-not-allowed":
      return SQL_HOST_NOT_ALLOWED_MESSAGE;
    case "invalid-path":
      return (
        `${message ? `${message} ` : ""}The .bak file must be inside the SQL Server's backup directory, and the ` +
        "data and log files inside its data directory, named .mdf (or .ndf) and .ldf."
      );
    default:
      return message || "The restore failed.";
  }
}

/**
 * A restore call's body: the action, this tab's query, the server and — only
 * here, for this one body — the password. A plain function, not a store method,
 * so MobX never sees the password as an action argument (dialRequest's rule).
 */
export function restoreRequest(
  action: string,
  query: Record<string, unknown>,
  server: Record<string, unknown>,
  password: string,
  extra: Record<string, unknown>
): Record<string, unknown> {
  return { action, ...query, config: password ? { ...server, password } : { ...server }, ...extra };
}

const isForbidden =(err: unknown) => (err as { code?: unknown } | null)?.code === 403;

const str = (v: unknown): string => (typeof v === "string" ? v : "");

/**
 * Data Mapper → Restore (ADMIN): read a .BAK's header and file list, list the
 * server's databases, and restore a .BAK under a new name. Ported from the
 * standalone tool's BAK restore panel.
 *
 * THE FILE IS ON THE SQL SERVER. `bakFilePath` is a path on the SQL Server's
 * own disk, inside its backup directory; the SQL Server opens it, HQ only sends
 * the path string and never uploads or reads the file.
 *
 * THE SERVER is the one filled in on the Data Source page (its form, without a
 * password); the password is typed here, held in the component, handed to one
 * call body and cleared — the same rule as the Data Source page. So `bakInfo`,
 * `listDatabases` and `restore` are not MobX actions (an action's arguments are
 * reported to spy listeners); their state writes go through runInAction.
 *
 * Gated twice in the UI (the route is `requiresAdmin`, and this store calls
 * nothing for a non-admin) and enforced by the API, which refuses with 403.
 */
class DataMapperRestoreStore {
  rootStore: RootStore;

  bakFilePath = "";
  databaseName = "";
  /** Optional: where the restored data file goes (a path on the SQL Server, in its data directory). */
  dataFilePath = "";
  /** Optional: where the restored log file goes (the same directory, .ldf). */
  logFilePath = "";
  overwrite = false;
  confirmOpen = false;

  busy: "bak-info" | "databases" | "restore" | null = null;
  bakInfo: BakInfo | null = null;
  databases: ServerDatabase[] = [];
  databasesCut = false;
  job: RestoreJobView = { status: "idle" };

  error: string | null = null;
  notice: string | null = null;
  /** The API answered 403: this account may not restore, whatever the UI believed. */
  forbidden = false;

  private disposeEvents: (() => void) | null = null;
  private epoch = 0;
  /** Bumped by every restore event: a status answer older than an event is not applied. */
  private eventSeq = 0;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, {
      rootStore: false,
      bakInfoFor: false,
      listDatabases: false,
      restore: false,
    });
  }

  private get selection() {
    return dataMapperSelection(this.rootStore);
  }

  private get sourceStore(): DataMapperSourceStore | null {
    return (this.rootStore as unknown as { dataMapperSourceViewStore?: DataMapperSourceStore })
      .dataMapperSourceViewStore ?? null;
  }

  get canUse(): boolean {
    return canUseDataMapper(this.rootStore);
  }

  get isAdmin(): boolean {
    return isAdmin(this.rootStore) && !this.forbidden;
  }

  get profileId(): string | null {
    return this.selection?.selectedProfileId ?? null;
  }

  get profile(): DataMapperProfile | null {
    const id = this.profileId;
    if (!id) return null;
    const all = (dataMapperService(this.rootStore, "data-mapper-profiles")?.entities ??
      []) as unknown as DataMapperProfile[];
    return all.find((p) => sameId(p.id, id)) ?? null;
  }

  /** The SQL Server from the Data Source page, without a password (null when none is filled in). */
  get server(): Record<string, unknown> | null {
    const config = this.sourceStore?.serverConfig;
    return config && str(config.server) ? config : null;
  }

  get serverLabel(): string {
    const s = this.server;
    if (!s) return "";
    const port = s.port ? `:${String(s.port)}` : "";
    const user = s.username ? ` as ${String(s.username)}` : "";
    return `${String(s.server)}${port}${user}`;
  }

  get nameValid(): boolean {
    return RESTORE_NAME_RE.test(this.databaseName);
  }

  get running(): boolean {
    return this.job.status === "running";
  }

  get canRestore(): boolean {
    return (
      this.isAdmin && !!this.server && this.bakFilePath.trim() !== "" && this.nameValid && !this.busy && !this.running
    );
  }

  /** The name already exists in the listed databases (a hint before the API says so). */
  get nameTaken(): boolean {
    return this.databases.some((d) => d.name.toLowerCase() === this.databaseName.toLowerCase());
  }

  /** The restore's progress as a whole percent, or null while it has none. */
  get percent(): number | null {
    const p = this.job.percent;
    return typeof p === "number" && Number.isFinite(p) ? Math.max(0, Math.min(100, p)) : null;
  }

  // ---- Route entry / exit ---------------------------------------------------------

  async enter(): Promise<void> {
    const authStore = (
      this.rootStore as unknown as { authStore?: { currentUser?: { id?: string | number } } }
    ).authStore;
    this.selection?.bindToUser(authStore?.currentUser?.id);
    if (!this.canUse || !this.isAdmin || !this.selection) return;
    if (!this.disposeEvents) {
      this.disposeEvents = subscribeTabEvents<RestoreEvent>(this.rootStore, {
        path: DM_RESTORE_PATH,
        events: RESTORE_EVENTS,
        tabId: this.selection.tabId,
        onEvent: (event, payload) => this.onRestoreEvent(event, payload),
      });
    }
    const clientId = this.selection.selectedClientId;
    await Promise.all([
      this.refreshStatus(),
      // So the header can name the profile after a reload.
      Promise.resolve()
        .then(() =>
          clientId ? dataMapperService(this.rootStore, "data-mapper-profiles")?.find({ query: { clientId } }) : undefined
        )
        .catch(() => undefined),
    ]);
  }

  /** The latest restore this tab started (a reload mid-restore resumes its progress). */
  async refreshStatus(): Promise<void> {
    const service = dataPlaneService(this.rootStore, "data-mapper-restore");
    const query = this.selection?.dataPlaneQuery;
    if (!service || !query || !this.isAdmin) return;
    const epoch = this.epoch;
    const seen = this.eventSeq;
    try {
      const view = (await service.create({ action: "status", ...query })) as RestoreJobView | null;
      runInAction(() => {
        // An event that landed while this was in flight is newer than the answer (Codex r1).
        if (epoch !== this.epoch || seen !== this.eventSeq || !view) return;
        this.job = view;
        // A failure found by this read (a reload, a missed event) reads like the event's (Codex r1).
        if (view.status === "error") {
          this.error = restoreErrorSentence(str(view.reason) || null, str(view.error), view.databaseName ?? this.databaseName);
        }
      });
    } catch (err) {
      if (isSessionEnded(err)) return;
      this.fail(epoch, err, "The restore's status could not be read.");
    }
  }

  onRestoreEvent(event: RestoreEvent, payload: DataMapperTabEventPayload) {
    const view = payload as unknown as RestoreJobView;
    // Only the restore this tab is showing (an earlier job's late event is dropped) —
    // unless a start is in flight, whose job's first event may beat its answer (Codex r1).
    if (this.job.id && view.id && view.id !== this.job.id && this.busy !== "restore") return;
    this.eventSeq++;
    if (event === DM_EVENTS.restoreProgress) {
      this.job = { ...this.job, ...view, status: "running" };
    } else if (event === DM_EVENTS.restoreComplete) {
      this.job = { ...this.job, ...view, status: "completed", percent: 100 };
      this.notice = `Restored "${view.databaseName ?? this.databaseName}".`;
    } else if (event === DM_EVENTS.restoreError) {
      this.job = { ...this.job, ...view, status: "error" };
      this.error = restoreErrorSentence(str(view.reason) || null, str(view.error), view.databaseName ?? this.databaseName);
    }
  }

  // ---- Form ---------------------------------------------------------------------

  setBakFilePath(value: string) {
    this.bakFilePath = value;
    this.bakInfo = null;
  }

  setDatabaseName(value: string) {
    this.databaseName = value.trim();
  }

  setDataFilePath(value: string) {
    this.dataFilePath = value;
  }

  setLogFilePath(value: string) {
    this.logFilePath = value;
  }

  setOverwrite(value: boolean) {
    this.overwrite = value;
  }

  openConfirm() {
    if (this.canRestore) this.confirmOpen = true;
  }

  closeConfirm() {
    this.confirmOpen = false;
  }

  clearError() {
    this.error = null;
  }

  clearNotice() {
    this.notice = null;
  }

  /** Fill the restore name from the backup's own database name, when none is typed. */
  useBackupName() {
    const name = this.bakInfo?.header[0]?.databaseName;
    if (name && !this.databaseName) this.databaseName = name.replace(/[^A-Za-z0-9_]/g, "_").slice(0, 128);
  }

  // ---- Calls (not actions: they take the password) ---------------------------------

  private begin(kind: NonNullable<DataMapperRestoreStore["busy"]>): number {
    runInAction(() => {
      this.busy = kind;
      this.error = null;
      this.notice = null;
    });
    return this.epoch;
  }

  private settle(epoch: number, apply: () => void) {
    runInAction(() => {
      if (epoch !== this.epoch) return;
      apply();
      this.busy = null;
    });
  }

  /** A failure as the page's sentence; a 403 also hides the admin controls. */
  private fail(epoch: number, err: unknown, fallback: string) {
    this.settle(epoch, () => {
      // The sign-out ended this call; the store was reset, say nothing.
      if (isSessionEnded(err)) return;
      // A 403 is the PERSON refused (admin-only, or no reason given) — or the
      // REQUEST refused (protected-database, sql-host-not-allowed), which must
      // not hide the form from an admin who can fix the request.
      const reason = refusalReason(err) ?? (isForbidden(err) ? "admin-only" : null);
      if (isForbidden(err) && reason === "admin-only") this.forbidden = true;
      this.error = restoreErrorSentence(reason, loadErrorMessage(err, fallback), this.databaseName);
    });
  }

  /** Where a call goes: this tab's query and the server (no password; see restoreRequest). */
  private get target(): { query: Record<string, unknown>; server: Record<string, unknown> } | null {
    const query = this.selection?.dataPlaneQuery;
    const server = this.server;
    return query && server ? { query: { ...query }, server } : null;
  }

  /** Read a .BAK's header and file list (the SQL Server reads the file). */
  async bakInfoFor(password: string): Promise<void> {
    const service = dataPlaneService(this.rootStore, "data-mapper-restore");
    const path = this.bakFilePath.trim();
    const t = this.target;
    const body = t && restoreRequest("bak-info", t.query, t.server, password, { restore: { bakFilePath: path } });
    if (!service || !body || !path || !this.isAdmin || this.busy) return;
    const epoch = this.begin("bak-info");
    try {
      const info = (await service.create(body)) as BakInfo | null;
      this.settle(epoch, () => {
        this.bakInfo = { header: info?.header ?? [], fileList: info?.fileList ?? [] };
        this.useBackupName();
      });
    } catch (err) {
      if (isSessionEnded(err)) return;
      this.fail(epoch, err, "The backup file could not be read.");
    }
  }

  /** The server's databases, following the byte pages. */
  async listDatabases(password: string): Promise<void> {
    const service = dataPlaneService(this.rootStore, "data-mapper-restore");
    // One server for every page: the password is never sent to a server the form
    // was changed to meanwhile, and two servers' lists never merge (Codex r1).
    const t = this.target;
    if (!service || !t || !this.isAdmin || this.busy) return;
    const epoch = this.begin("databases");
    try {
      const all: ServerDatabase[] = [];
      let skip: number | null = 0;
      // shortcut: each page is a fresh dial; at most MAX_DATABASE_PAGES of them.
      for (let page = 0; skip !== null && page < MAX_DATABASE_PAGES; page++) {
        if (epoch !== this.epoch) return;
        const body = restoreRequest("list-databases", t.query, t.server, password, { $skip: skip });
        const answer = (await service.create(body)) as {
          rows?: ServerDatabase[];
          nextCursor?: number | null;
        } | null;
        all.push(...(answer?.rows ?? []).filter((r) => typeof r?.name === "string"));
        skip = typeof answer?.nextCursor === "number" && answer.nextCursor > skip ? answer.nextCursor : null;
      }
      const cut = skip !== null;
      this.settle(epoch, () => {
        this.databases = all;
        this.databasesCut = cut;
      });
    } catch (err) {
      if (isSessionEnded(err)) return;
      this.fail(epoch, err, "The databases could not be listed.");
    }
  }

  /** Start the restore (after the confirm). Progress and the end arrive as events. */
  async restore(password: string): Promise<boolean> {
    const service = dataPlaneService(this.rootStore, "data-mapper-restore");
    const databaseName = this.databaseName;
    const dataFilePath = this.dataFilePath.trim();
    const logFilePath = this.logFilePath.trim();
    // The overrides are checked by the API (confined to the SQL Server's data directory, like the .bak path).
    const restore = {
      bakFilePath: this.bakFilePath.trim(),
      databaseName,
      overwrite: this.overwrite,
      ...(dataFilePath ? { dataFilePath } : {}),
      ...(logFilePath ? { logFilePath } : {}),
    };
    const t = this.target;
    const body = t && restoreRequest("restore", t.query, t.server, password, { restore });
    runInAction(() => {
      this.confirmOpen = false;
    });
    if (!service || !body || !this.canRestore) return false;
    const epoch = this.begin("restore");
    try {
      const answer = (await service.create(body)) as { started?: boolean; restore?: RestoreJobView } | null;
      this.settle(epoch, () => {
        const started = answer?.restore ?? { status: "running", databaseName, percent: null };
        // The job's first event may have beaten this answer: never rewind it (Codex r1).
        if (!started.id || started.id !== this.job.id) this.job = started;
      });
      return true;
    } catch (err) {
      if (isSessionEnded(err)) return false;
      this.fail(epoch, err, "The restore could not be started.");
      return false;
    }
  }

  /** Point the Data Source page at the restored database and go there. */
  useRestoredDatabase() {
    const name = this.job.databaseName;
    if (!name || this.job.status !== "completed") return;
    this.sourceStore?.setFormField("database", name);
    void this.rootStore.routerStore?.goTo(DATA_MAPPER_SOURCE_ROUTE);
  }

  goToSource() {
    void this.rootStore.routerStore?.goTo(DATA_MAPPER_SOURCE_ROUTE);
  }

  /** Route exit: stop listening; drop messages and what was read from the server. The form stays. */
  reset() {
    this.disposeEvents?.();
    this.disposeEvents = null;
    this.epoch++;
    this.busy = null;
    this.confirmOpen = false;
    this.error = null;
    this.notice = null;
    this.bakInfo = null;
    this.databases = [];
    this.databasesCut = false;
    this.job = { status: "idle" };
  }

  /**
   * Everything reset() keeps across this wizard's route changes goes too: on
   * sign-out it was the previous person's. Called by RootStore.purgeUserData,
   * which prefers this to reset().
   */
  resetForSignOut() {
    this.reset();
    this.bakFilePath = "";
    this.databaseName = "";
    this.dataFilePath = "";
    this.logFilePath = "";
    this.overwrite = false;
    this.forbidden = false;
  }
}

export default DataMapperRestoreStore;
